import { useMemo, useState, useEffect, useRef } from 'react';
import { todayYmd } from '@/features/tickets/services/ticketPresentation';

function clientId(client = {}) {
  return client.Client_Id || client['Client ID'] || '';
}

function clientName(client = {}) {
  return client['Client Name'] || client.Name || client.Client || clientId(client);
}

function userName(user = {}) {
  return user['Employee Name'] || user.Name || user.name || user['EMP Code'] || '';
}

function userId(user = {}) {
  return user['EMP Code'] || user['Employee ID'] || user.id || '';
}

function getUserDesignation(user = {}) {
  const raw = String(user.Role || user.role || user.Designation || user.designation || '').trim();
  const lower = raw.toLowerCase();
  if (lower === 'user' || lower === 'employee') return 'Employee';
  if (lower === 'manager') return 'Manager';
  if (lower === 'hr') return 'HR';
  if (lower === 'super admin' || lower === 'admin') return 'Super Admin';
  if (lower === 'pc') return 'PC';
  return raw || 'Employee';
}

export function AutoTicketDialog({ ticket, clients, categories, users, projects = [], saving, onSubmit, onClose }) {
  const isEditing = Boolean(ticket);
  
  const initialFrequency = isEditing ? (ticket.Frequency || ticket.Frequence || 'Daily') : 'Daily';
  const isMonthly = /^\d+$/.test(initialFrequency) || ['1st Fri', '2nd Fri', '3rd Fri', '4th Fri'].includes(initialFrequency);
  const [frequencyType, setFrequencyType] = useState(isMonthly ? 'Monthly' : 'Weekly');

  const initialUser = useMemo(() => {
    const empId = isEditing ? (ticket['Employee ID'] || ticket.EmpID || '') : '';
    return users.find(u => userId(u) === empId);
  }, [isEditing, ticket, users]);

  const [selectedDesignation, setSelectedDesignation] = useState(
    initialUser ? getUserDesignation(initialUser) : ''
  );

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(() => {
    if (isEditing) {
      const empId = ticket?.['Employee ID'] || ticket?.EmpID || '';
      return empId ? [empId] : [];
    }
    return [];
  });

  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [validationError, setValidationError] = useState('');
  const employeeDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (initialUser) {
      setSelectedDesignation(getUserDesignation(initialUser));
      const empId = userId(initialUser);
      if (empId) setSelectedEmployeeIds([empId]);
    }
  }, [initialUser]);

  const designationList = useMemo(() => {
    const order = ['Employee', 'Manager', 'HR', 'Super Admin', 'PC'];
    const set = new Set();
    users.forEach(u => {
      const d = getUserDesignation(u);
      if (d) set.add(d);
    });
    return [...set].sort((a, b) => {
      const idxA = order.indexOf(a);
      const idxB = order.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!selectedDesignation) return [];
    return users.filter(u => getUserDesignation(u).toLowerCase() === selectedDesignation.toLowerCase());
  }, [users, selectedDesignation]);

  const searchedFilteredUsers = useMemo(() => {
    if (!empSearch.trim()) return filteredUsers;
    const q = empSearch.toLowerCase();
    return filteredUsers.filter(u =>
      userName(u).toLowerCase().includes(q) ||
      userId(u).toLowerCase().includes(q)
    );
  }, [filteredUsers, empSearch]);

  const [formData, setFormData] = useState({
    clientId: isEditing ? (ticket.Client_Id || ticket['Client ID'] || '') : (clients.length ? clientId(clients[0]) : ''),
    category: isEditing ? ticket['Task Category'] : (categories[0] || 'General'),
    description: isEditing ? (ticket['Task Description'] || '') : '',
    priority: isEditing ? ticket.Priority : 'Normal',
    planDate: isEditing ? ticket['Plan Date'] : todayYmd(),
    tat: isEditing ? (ticket.TAT || '60') : '60',
    project: isEditing ? (ticket.Project || ticket['Project Name'] || '') : '',
    frequency: initialFrequency,
    attachment: isEditing ? (ticket.Attachment || '') : ''
  });

  const projectOptions = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const set = new Set();
    list.forEach((p) => {
      const name = typeof p === 'object' ? (p.Name || p['Project Name'] || p.name) : p;
      const clean = String(name || '').trim();
      if (clean) set.add(clean);
    });
    if (formData.project && String(formData.project).trim()) {
      set.add(String(formData.project).trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [projects, formData.project]);

  const handleUpdate = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDesignationChange = (e) => {
    const newDesig = e.target.value;
    setSelectedDesignation(newDesig);
    const matching = users.filter(u => getUserDesignation(u).toLowerCase() === newDesig.toLowerCase());
    setSelectedEmployeeIds(prev => prev.filter(id => matching.some(u => userId(u) === id)));
    setEmpSearch('');
    setValidationError('');
  };

  const toggleEmployee = (empId) => {
    setValidationError('');
    setSelectedEmployeeIds(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredUsers.map(u => userId(u)).filter(Boolean);
    const areAllSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedEmployeeIds.includes(id));
    if (areAllSelected) {
      setSelectedEmployeeIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      setSelectedEmployeeIds(prev => Array.from(new Set([...prev, ...allFilteredIds])));
    }
    setValidationError('');
  };

  const employeeTriggerLabel = useMemo(() => {
    if (!selectedDesignation) return 'Select Designation First';
    if (selectedEmployeeIds.length === 0) return 'Select Employee(s)...';
    if (filteredUsers.length > 0 && selectedEmployeeIds.length === filteredUsers.length) {
      return `All ${filteredUsers.length} Employees Selected`;
    }
    const names = selectedEmployeeIds
      .map(id => {
        const u = users.find(user => userId(user) === id);
        return u ? userName(u) : id;
      })
      .filter(Boolean);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]}, ${names[1]}`;
    return `${names[0]}, ${names[1]} +${names.length - 2} more`;
  }, [selectedDesignation, selectedEmployeeIds, filteredUsers, users]);

  const handleFrequencyTypeChange = (e) => {
    const newType = e.target.value;
    setFrequencyType(newType);
    handleUpdate('frequency', newType === 'Weekly' ? 'Daily' : '1');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedEmployeeIds.length === 0) {
      setValidationError('Please select at least one Employee (Help Person).');
      return;
    }

    const client = clients.find(c => clientId(c) === formData.clientId) || {};
    const projectName = (formData.project || '').trim();

    // If editing a single ticket and 1 employee is selected:
    if (isEditing && selectedEmployeeIds.length === 1) {
      const empId = selectedEmployeeIds[0];
      const user = users.find(u => userId(u) === empId) || {};
      const payload = {
        Client_Id: formData.clientId,
        'Client ID': formData.clientId,
        Name: clientName(client),
        'Client Name': clientName(client),
        Project: projectName,
        'Project Name': projectName,
        'Task Category': formData.category,
        'Task Description': formData.description,
        Priority: formData.priority,
        'Plan Date': formData.planDate,
        'Employee ID': empId,
        'Help Person Name': userName(user),
        Designation: selectedDesignation || getUserDesignation(user),
        TAT: Number(formData.tat),
        Frequency: formData.frequency,
        Attachment: formData.attachment,
        'Auto Ticket': 'Yes',
        'Is Auto Ticket': 'Yes',
        'Ticket ID': ticket['Ticket ID'],
        legacyId: ticket.legacyId
      };
      onSubmit(payload);
      return;
    }

    // Creating new or multi-employee KRA:
    const payloads = selectedEmployeeIds.map((empId, idx) => {
      const user = users.find(u => userId(u) === empId) || {};
      return {
        Client_Id: formData.clientId,
        'Client ID': formData.clientId,
        Name: clientName(client),
        'Client Name': clientName(client),
        Project: projectName,
        'Project Name': projectName,
        'Task Category': formData.category,
        'Task Description': formData.description,
        Priority: formData.priority,
        'Plan Date': formData.planDate,
        'Employee ID': empId,
        'Help Person Name': userName(user),
        Designation: selectedDesignation || getUserDesignation(user),
        TAT: Number(formData.tat),
        Frequency: formData.frequency,
        Attachment: formData.attachment,
        'Auto Ticket': 'Yes',
        'Is Auto Ticket': 'Yes',
        'Ticket ID': `TKT_${Date.now()}_${idx}`
      };
    });

    onSubmit(payloads.length === 1 ? payloads[0] : payloads);
  };

  return (
    <form className="ticket-form-grid" onSubmit={handleSubmit}>
      <fieldset style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '16px', alignItems: 'start' }}>
        <legend>{isEditing ? 'Edit KRA Template' : 'Design New KRA Template'}</legend>
        
        {/* Row 1 */}
        <label className="dashboard-control">
          <span>Project Name (Optional)</span>
          <select
            value={formData.project || ''}
            onChange={(e) => handleUpdate('project', e.target.value)}
          >
            <option value="">Select Project (Optional)</option>
            {projectOptions.map((p) => (
              <option key={p} value={p}>
                📁 {p}
              </option>
            ))}
          </select>
        </label>
        
        <label className="dashboard-control">
          <span>Client</span>
          <select value={formData.clientId} onChange={e => handleUpdate('clientId', e.target.value)} required>
            <option value="">Select Client</option>
            {clients.map(c => <option key={clientId(c)} value={clientId(c)}>{clientName(c)}</option>)}
          </select>
        </label>
        
        <label className="dashboard-control">
          <span>Task Category</span>
          <select value={formData.category} onChange={e => handleUpdate('category', e.target.value)}>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </label>

        {/* Row 2 */}
        <label className="dashboard-control">
          <span>Designation</span>
          <select value={selectedDesignation} onChange={handleDesignationChange} required>
            <option value="">Select Designation</option>
            {designationList.map(desig => (
              <option key={desig} value={desig}>{desig}</option>
            ))}
          </select>
        </label>

        {/* Multi-Select Employee (Help Person) */}
        <div className="dashboard-control" style={{ position: 'relative' }} ref={employeeDropdownRef}>
          <span>Employee (Help Person)</span>
          
          <button
            type="button"
            disabled={!selectedDesignation}
            onClick={() => setIsEmployeeDropdownOpen(prev => !prev)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              height: '44px',
              textAlign: 'left',
              backgroundColor: !selectedDesignation ? '#f8fafc' : '#fcfcfc',
              cursor: !selectedDesignation ? 'not-allowed' : 'pointer',
              border: validationError ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
              boxShadow: validationError ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : 'none',
              borderRadius: '10px',
              padding: '0 14px',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: selectedEmployeeIds.length ? '#1e293b' : '#94a3b8',
              boxSizing: 'border-box',
              outline: 'none',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', minWidth: 0, flex: 1 }}>
              {selectedEmployeeIds.length > 1 && (
                <span style={{
                  background: '#eff6ff',
                  color: '#2563eb',
                  fontWeight: 600,
                  fontSize: '11px',
                  padding: '2px 7px',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                  flexShrink: 0,
                  lineHeight: '1.2'
                }}>
                  {selectedEmployeeIds.length} Selected
                </span>
              )}
              <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: selectedEmployeeIds.length ? '#1e293b' : '#94a3b8',
                fontSize: '14px'
              }}>
                {employeeTriggerLabel}
              </span>
            </div>
            
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#64748b"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                flexShrink: 0,
                marginLeft: '8px',
                transition: 'transform 0.2s',
                transform: isEmployeeDropdownOpen ? 'rotate(180deg)' : 'none'
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isEmployeeDropdownOpen && selectedDesignation && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
                maxHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Search Bar + Select All Header */}
              <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <input
                  type="text"
                  value={empSearch}
                  onChange={e => setEmpSearch(e.target.value)}
                  placeholder="Search employee name or ID..."
                  style={{
                    width: '100%',
                    padding: '7px 10px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1.5px solid #cbd5e1',
                    boxSizing: 'border-box',
                    outline: 'none',
                    marginBottom: '6px'
                  }}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>
                    {selectedEmployeeIds.length} of {filteredUsers.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2563eb',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: '2px 4px',
                      borderRadius: '4px'
                    }}
                  >
                    {filteredUsers.length > 0 && filteredUsers.every(u => selectedEmployeeIds.includes(userId(u)))
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                </div>

                {/* Selected Chips inside dropdown */}
                {selectedEmployeeIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px', maxHeight: '56px', overflowY: 'auto' }}>
                    {selectedEmployeeIds.map(id => {
                      const u = users.find(user => userId(user) === id);
                      return (
                        <span
                          key={id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#1e40af',
                            fontSize: '11px',
                            padding: '1px 6px',
                            borderRadius: '10px',
                            lineHeight: '1.2'
                          }}
                        >
                          <span>{u ? userName(u) : id}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEmployee(id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#3b82f6',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              padding: 0,
                              lineHeight: 1
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* User List with Checkboxes */}
              <div style={{ overflowY: 'auto', maxHeight: '200px', padding: '4px 0' }}>
                {searchedFilteredUsers.length === 0 ? (
                  <div style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    No employees found in {selectedDesignation}
                  </div>
                ) : (
                  searchedFilteredUsers.map(u => {
                    const uId = userId(u);
                    const isSelected = selectedEmployeeIds.includes(uId);
                    return (
                      <div
                        key={uId}
                        onClick={() => toggleEmployee(uId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          background: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={e => {
                          if (!isSelected) e.currentTarget.style.background = '#f1f5f9';
                        }}
                        onMouseLeave={e => {
                          if (!isSelected) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // handled by parent div click
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#2563eb' }}
                        />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: isSelected ? 600 : 400, color: isSelected ? '#1e40af' : '#1e293b' }}>
                            {userName(u)}
                          </span>
                          <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', marginLeft: '6px' }}>
                            {uId}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <label className="dashboard-control">
          <span>Priority</span>
          <select value={formData.priority} onChange={e => handleUpdate('priority', e.target.value)}>
            <option>Normal</option>
            <option>Low</option>
            <option>High</option>
            <option>Urgent</option>
            <option>Super Urgent</option>
          </select>
        </label>

        {/* Row 3 */}
        <label className="dashboard-control">
          <span>Frequency Type</span>
          <select value={frequencyType} onChange={handleFrequencyTypeChange} required>
            <option value="Weekly">Weekly (Includes Daily)</option>
            <option value="Monthly">Monthly</option>
          </select>
        </label>

        <label className="dashboard-control">
          <span>{frequencyType === 'Weekly' ? 'Day of Week' : 'Date / Occurence'}</span>
          <select value={formData.frequency} onChange={e => handleUpdate('frequency', e.target.value)} required>
            {frequencyType === 'Weekly' ? (
              <>
                <option value="Daily">Daily</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
              </>
            ) : (
              <>
                {Array.from({ length: 31 }, (_, i) => String(i + 1)).map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
                <option value="1st Fri">1st Fri</option>
                <option value="2nd Fri">2nd Fri</option>
                <option value="3rd Fri">3rd Fri</option>
                <option value="4th Fri">4th Fri</option>
              </>
            )}
          </select>
        </label>

        <label className="dashboard-control">
          <span>TAT (mins)</span>
          <input type="number" min="1" value={formData.tat} onChange={e => handleUpdate('tat', e.target.value)} required />
        </label>

        {/* Row 4 */}
        <label className="dashboard-control" style={{ gridColumn: '1 / -1' }}>
          <span>Attachment (Link or File Name)</span>
          <input type="text" placeholder="e.g. Google Drive link or file name..." value={formData.attachment} onChange={e => handleUpdate('attachment', e.target.value)} />
        </label>
        
        {/* Row 5 */}
        <label className="dashboard-control" style={{ gridColumn: '1 / -1' }}>
          <span>Task Description</span>
          <textarea value={formData.description} onChange={e => handleUpdate('description', e.target.value)} rows="4" placeholder="Enter detailed task description..." required />
        </label>

      </fieldset>
      
      <div className="ticket-form-actions" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
        <button type="button" className="attendance-cta attendance-cta--red" style={{ padding: '6px 16px', minHeight: '36px', fontSize: '14px', width: 'auto', flex: '0 0 auto' }} onClick={onClose} disabled={saving}>Cancel</button>
        <button type="submit" className="attendance-cta attendance-cta--blue" style={{ padding: '6px 16px', minHeight: '36px', fontSize: '14px', width: 'auto', flex: '0 0 auto' }} disabled={saving}>{saving ? 'Saving...' : 'Save Design'}</button>
      </div>
    </form>
  );
}
